import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  EmptyState,
  FilterBar,
  FilterSelect,
  SearchField,
  SectionTitle,
  SortButton,
  palette,
} from '../dashboardPrimitives';
import {
  PATROLLER_COVERAGE_OPTIONS,
  PATROLLER_MONTH_DATA,
  PATROLLER_RATE_OPTIONS,
  PATROLLER_TABLE_COLUMNS,
  formatPatrollerCount,
  formatPatrollerKm,
  formatPatrollerPercent,
  matchesPatrollerCoverageFilter,
  matchesPatrollerRateFilter,
  patrollerCoverageTone,
  patrollerRateTone,
} from '../../data/patrollerData';

const GROUP_HEADERS = [
  { label: 'Identity', span: 5 },
  { label: '1.8A App Compliance', span: 3 },
  { label: '1.8B Route Coverage', span: 4 },
  { label: 'Spans', span: 2 },
];

const CIRCLE_CHIP_CLASSES = {
  HP: 'bg-blue-50 text-blue-700',
  JA: 'bg-pink-50 text-pink-700',
  KS: 'bg-violet-50 text-violet-700',
  UW: 'bg-emerald-50 text-emerald-700',
};

const BAR_TONE_CLASSES = {
  red: { fill: 'bg-red-500', text: 'text-red-500' },
  amber: { fill: 'bg-amber-500', text: 'text-amber-600' },
  olive: { fill: 'bg-lime-600', text: 'text-lime-700' },
  green: { fill: 'bg-emerald-500', text: 'text-emerald-600' },
  slate: { fill: 'bg-slate-400', text: 'text-slate-500' },
};

function sortPatrollers(rows, sortState) {
  const sortedRows = [...rows];
  sortedRows.sort((left, right) => {
    const leftValue = left[sortState.key];
    const rightValue = right[sortState.key];
    if (typeof leftValue === 'string') {
      return sortState.dir === 'asc'
        ? leftValue.localeCompare(rightValue)
        : rightValue.localeCompare(leftValue);
    }
    return sortState.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
  });
  return sortedRows;
}

function columnAlignmentClass(align) {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function circleChipClass(circle) {
  return CIRCLE_CHIP_CLASSES[circle] || 'bg-slate-100 text-slate-600';
}

function metricToneClass(tone) {
  return BAR_TONE_CLASSES[tone] || BAR_TONE_CLASSES.slate;
}

function MetricBar({ value, tone }) {
  const toneClasses = metricToneClass(tone);
  const capped = Math.max(0, Math.min(value || 0, 100));

  return (
    <div className="flex min-w-[130px] items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${toneClasses.fill}`} style={{ width: `${capped}%` }} />
      </div>
      <span className={`mono-cell text-[11px] font-semibold ${toneClasses.text}`}>
        {formatPatrollerPercent(value)}
      </span>
    </div>
  );
}

function MetricTableCell({ row, column }) {
  switch (column.key) {
    case 'circle':
      return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] ${circleChipClass(row.circle)}`}>
          {row.circle}
        </span>
      );
    case 'code':
      return <span className="mono-cell text-[11px] text-slate-500">{row.code}</span>;
    case 'j':
    case 'aa':
    case 'sp':
    case 'as_':
      return <span className="mono-cell">{formatPatrollerCount(row[column.key])}</span>;
    case 'u':
    case 'k':
    case 'z':
      return <span className="mono-cell">{formatPatrollerKm(row[column.key])}</span>;
    case 'ar':
      return <MetricBar value={row.ar} tone={patrollerRateTone(row.ar)} />;
    case 'cv':
      return <MetricBar value={row.cv} tone={patrollerCoverageTone(row.cv)} />;
    default:
      return row[column.key];
  }
}

function SpanMetricBar({ value, tone, dashWhenZero = false }) {
  if (dashWhenZero) {
    return <span className="mono-cell text-[11px] text-slate-400">-</span>;
  }

  return <MetricBar value={value} tone={tone} />;
}

function SpanDrilldown({ row }) {
  return (
    <div className="rounded-2xl border border-app-border bg-slate-50/80 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-slate-800">Span Drilldown - {row.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] ${circleChipClass(row.circle)}`}>
              {row.circle}
            </span>
            <span>{row.mp}</span>
            <span className="text-slate-300">|</span>
            <span>{row.nw_type}</span>
            <span className="text-slate-300">|</span>
            <span className="mono-cell">{row.code}</span>
          </div>
        </div>
        <div className="text-right text-[11px] leading-5 text-slate-500">
          <div>{formatPatrollerCount(row.sp)} assigned spans</div>
          <div>{formatPatrollerCount(row.as_)} active spans</div>
        </div>
      </div>

      <div className="mt-4 table-scroll-shell-bordered">
        <table className="evidence-table">
          <thead>
            <tr>
              <th>Span ID</th>
              <th>NW Type</th>
              <th className="text-right">Assigned KM</th>
              <th className="text-right">1.8B Scope</th>
              <th className="text-right">Patrol KM</th>
              <th className="text-center">Days</th>
              <th className="text-center">Compliant Days</th>
              <th>1.8A Rate</th>
              <th>1.8B Coverage</th>
            </tr>
          </thead>
          <tbody>
            {row.spans.map((span) => {
              const inactive = span.aa === 0;
              return (
                <tr key={span.id} className={inactive ? 'bg-slate-50/70' : ''}>
                  <td className="mono-cell text-slate-500">{span.id}</td>
                  <td>{span.nw}</td>
                  <td className="text-right mono-cell">{formatPatrollerKm(span.u, 3)}</td>
                  <td className="text-right mono-cell">{formatPatrollerKm(span.k)}</td>
                  <td className="text-right mono-cell">{formatPatrollerKm(span.z)}</td>
                  <td className="text-center mono-cell">{formatPatrollerCount(span.aa)}</td>
                  <td className="text-center mono-cell">{formatPatrollerCount(span.j)}</td>
                  <td>
                    <SpanMetricBar
                      value={span.ar}
                      tone={patrollerRateTone(span.ar)}
                      dashWhenZero={span.aa === 0}
                    />
                  </td>
                  <td>
                    <SpanMetricBar
                      value={span.cv}
                      tone={patrollerCoverageTone(span.cv)}
                      dashWhenZero={span.k === 0}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PatrollerDrilldown({ selectedMonth = "Dec'25" }) {
  const monthData = PATROLLER_MONTH_DATA[selectedMonth] || PATROLLER_MONTH_DATA["Dec'25"];
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [activeMp, setActiveMp] = useState('ALL');
  const [activeNwType, setActiveNwType] = useState('ALL');
  const [activeRateFilter, setActiveRateFilter] = useState('ALL');
  const [activeCoverageFilter, setActiveCoverageFilter] = useState('ALL');
  const [searchValue, setSearchValue] = useState('');
  const [sortState, setSortState] = useState({ key: 'ar', dir: 'asc' });
  const [expandedCode, setExpandedCode] = useState(null);

  const rows = monthData.rows;

  const circleOptions = useMemo(() => ([
    { value: 'ALL', label: 'All Circles' },
    ...Array.from(new Set(rows.map((row) => row.circle))).sort().map((circle) => ({
      value: circle,
      label: circle,
    })),
  ]), [rows]);

  const mpOptions = useMemo(() => ([
    { value: 'ALL', label: 'All MPs' },
    ...Array.from(new Set(rows.map((row) => row.mp))).sort().map((mp) => ({
      value: mp,
      label: mp,
    })),
  ]), [rows]);

  const nwTypeOptions = useMemo(() => ([
    { value: 'ALL', label: 'All NW Types' },
    { value: 'Intercity', label: 'Intercity' },
    { value: 'Intracity', label: 'Intracity' },
  ]), []);

  const filteredRows = useMemo(() => {
    const query = searchValue.toLowerCase().trim();

    return rows.filter((row) => {
      if (activeCircle !== 'ALL' && row.circle !== activeCircle) return false;
      if (activeMp !== 'ALL' && row.mp !== activeMp) return false;
      if (activeNwType !== 'ALL' && !row.nw_type.includes(activeNwType)) return false;
      if (!matchesPatrollerRateFilter(row.ar, activeRateFilter)) return false;
      if (!matchesPatrollerCoverageFilter(row.cv, activeCoverageFilter)) return false;

      if (!query) return true;

      return (
        row.name.toLowerCase().includes(query)
        || row.code.toLowerCase().includes(query)
        || row.spans.some((span) => span.id.toLowerCase().includes(query))
      );
    });
  }, [
    activeCircle,
    activeCoverageFilter,
    activeMp,
    activeNwType,
    activeRateFilter,
    rows,
    searchValue,
  ]);

  const sortedRows = useMemo(() => sortPatrollers(filteredRows, sortState), [filteredRows, sortState]);

  useEffect(() => {
    if (!expandedCode) return;
    if (!sortedRows.some((row) => row.code === expandedCode)) {
      setExpandedCode(null);
    }
  }, [expandedCode, sortedRows]);

  const visibleSummary = `${formatPatrollerCount(sortedRows.length)} of ${formatPatrollerCount(rows.length)} patrollers`;

  const updateSort = (key) => {
    setSortState((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleExpand = (code) => {
    setExpandedCode((current) => (current === code ? null : code));
  };

  return (
    <div className="card-surface p-6">
      <SectionTitle dot={palette.navySoft}>Patroller Drilldown</SectionTitle>

      <FilterBar className="mt-4">
          <FilterSelect label="Circle" value={activeCircle} onChange={setActiveCircle} options={circleOptions} />
          <FilterSelect label="MP" value={activeMp} onChange={setActiveMp} options={mpOptions} />
          <FilterSelect label="NW Type" value={activeNwType} onChange={setActiveNwType} options={nwTypeOptions} />
          <FilterSelect label="1.8A Rate" value={activeRateFilter} onChange={setActiveRateFilter} options={PATROLLER_RATE_OPTIONS} />
          <FilterSelect
            label="1.8B Coverage"
            value={activeCoverageFilter}
            onChange={setActiveCoverageFilter}
            options={PATROLLER_COVERAGE_OPTIONS}
          />
          <SearchField
            label="Search"
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Patroller name, code, or span ID"
            className="min-w-[260px] xl:ml-auto"
          />
      </FilterBar>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-[12px] text-slate-500">{visibleSummary} · 10 rows visible per scroll view</div>
        <div className="text-[11px] uppercase tracking-[0.08em] text-slate-400">
          Default order: 1.8A rate, worst first
        </div>
      </div>

      {sortedRows.length ? (
        <div className="mt-4 table-scroll-shell-double-head-bordered">
          <table className="evidence-table">
            <thead>
              <tr>
                <th className="sticky top-0 z-30 w-11 bg-slate-100 shadow-[0_1px_0_0_#E2E8F0]" />
                {GROUP_HEADERS.map((group) => (
                  <th
                    key={group.label}
                    colSpan={group.span}
                    className="sticky top-0 z-30 bg-slate-100 text-center text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500 shadow-[0_1px_0_0_#E2E8F0]"
                  >
                    {group.label}
                  </th>
                ))}
              </tr>
              <tr className="col-row">
                <th className="sticky top-[41px] z-20 bg-slate-50 shadow-[0_1px_0_0_#E2E8F0]" />
                {PATROLLER_TABLE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`sticky top-[41px] z-20 bg-slate-50 shadow-[0_1px_0_0_#E2E8F0] ${columnAlignmentClass(column.align)}`}
                  >
                    <SortButton
                      label={column.label}
                      sortKey={column.key}
                      sortState={sortState}
                      onSort={updateSort}
                      className={column.align === 'right' ? 'justify-end w-full' : column.align === 'center' ? 'justify-center w-full' : ''}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const isExpanded = expandedCode === row.code;

                return (
                  <Fragment key={row.code}>
                    <tr
                      className={`cursor-pointer ${isExpanded ? 'bg-blue-50/40' : ''}`}
                      onClick={() => toggleExpand(row.code)}
                    >
                      <td className="text-center">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-app-border bg-white text-slate-500 transition-colors duration-150 hover:bg-slate-50"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpand(row.code);
                          }}
                          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      {PATROLLER_TABLE_COLUMNS.map((column) => (
                        <td key={column.key} className={columnAlignmentClass(column.align)}>
                          <MetricTableCell row={row} column={column} />
                        </td>
                      ))}
                    </tr>
                    {isExpanded ? (
                      <tr>
                        <td colSpan={PATROLLER_TABLE_COLUMNS.length + 1} className="bg-slate-50/60 px-4 py-4">
                          <SpanDrilldown row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No patrollers match the selected filters" className="mt-4" />
      )}

      <div className="mt-4 text-[11px] text-slate-400">
        Aerial Telecom - Jio Ops Fiber KPI - {selectedMonth} - Source: {monthData.sourceLabel} - {formatPatrollerCount(rows.length)} patrollers
      </div>
    </div>
  );
}
